import React, { useState } from "react";
import Axios from "axios";
import { NotificationManager } from "react-notifications";
import {
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import { Button, Field } from "../../../components";
import { CardElementsProvider } from "context/stripe-provider";
import useLanguage from "../../../hooks/language";
import ENDPOINTS from "../../../utils/endpoints";
import Appointment from "../../../models/appointment";
import ExpressWalletCheckout, {
    NO_SHOW_FEE_WITH_TAX_CENTS,
    WalletCheckoutFlow,
} from "../../../pages/home/screening/payment-field/express-wallet-checkout";
import PaymentMethodsBusy from "../../../pages/home/screening/payment-field/payment-methods-busy";
import formStyles from "./payment-form.module.css";

const elementOptions = {
    style: {
        base: {
            fontFamily: '"Poppins" sans-serif',
            fontSize: "16px",
            border: "1px solid #1C1C1C",
            fontWeight: "400",
            color: "#1C1C1C",
        },
    },
};

interface PaymentFormProps {
    appointment?: Appointment;
    datetime: string | null;
    clinicId: number | null;
    onCompletePayment?: () => void;
    onClose?: () => void;
}

type FeePayment = {
    token?: string;
    paymentMethodId?: string;
};

const CardPaymentFields: React.FunctionComponent<{
    onClose?: () => void;
    isBusy: boolean;
    payFees: (payment: FeePayment) => Promise<void>;
    showCardIntro: boolean;
}> = ({ onClose, isBusy, payFees, showCardIntro }) => {
    const { translations } = useLanguage();
    const stripe = useStripe();
    const elements = useElements();
    const t = translations.paymentSection;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [cardHolderName, setCardHolderName] = useState("");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [postalCode, setPostalCode] = useState("");

    const enabled =
        cardHolderName.length > 0 &&
        address.length > 0 &&
        city.length > 0 &&
        postalCode.length > 0;

    const onSubmit = async () => {
        if (!stripe || !elements) return;

        setIsSubmitting(true);

        const cardNumberElement = elements.getElement(CardNumberElement);

        if (!cardNumberElement) {
            setIsSubmitting(false);
            return;
        }

        try {
            const { token, error } = await stripe.createToken(
                cardNumberElement,
                {
                    name: cardHolderName,
                    address_zip: postalCode,
                    address_line1: address,
                    address_city: city,
                }
            );

            if (error) {
                NotificationManager.error(error.message);
                console.error(error);
                return;
            }

            if (token) {
                await payFees({ token: token.id });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const busy = isBusy || isSubmitting;

    return (
        <div
            className={
                !showCardIntro ? formStyles.fieldsAfterWallet : undefined
            }
        >
            {showCardIntro && (
                <p>
                    {
                        translations.appointmentsSection.gatherCreditInfoModal
                            .content
                    }
                    <a
                        href={
                            translations.appointmentsSection.creditInfoModal
                                .faqUrl
                        }
                        target="_blank"
                        rel="noreferrer"
                    >
                        {
                            translations.appointmentsSection
                                .gatherCreditInfoModal.link
                        }
                    </a>
                </p>
            )}

            <div className="row">
                <div className="input-holder hundred">
                    <label>{t.cardHolderName}</label>
                    <Field
                        value={cardHolderName}
                        onChange={(e) => setCardHolderName(e.target.value)}
                    />
                </div>
            </div>

            <div className="row">
                <div className="input-holder hundred">
                    <label>{t.cardNumber}</label>
                    <div className="stripe-element-wrapper">
                        <CardNumberElement options={elementOptions} />
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="input-holder fifty">
                    <label>{t.cardExpirationDate}</label>
                    <div className="stripe-element-wrapper">
                        <CardExpiryElement options={elementOptions} />
                    </div>
                </div>
                <div className="input-holder fifty">
                    <label>{t.cardSecurityCode}</label>
                    <div className="stripe-element-wrapper">
                        <CardCvcElement options={elementOptions} />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="input-holder thirty">
                    <label>{t.cardHolderAddress}</label>
                    <Field
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                    />
                </div>
                <div className="input-holder thirty">
                    <label>{t.cardHolderCity}</label>
                    <Field
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                    />
                </div>
                <div className="input-holder thirty">
                    <label>{t.cardHolderZipCode}</label>
                    <Field
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                    />
                </div>
            </div>

            <br />

            <p className="disclaimer">{t.privacyMessage}</p>
            <p className="disclaimer">
                <b>{t.delayDisclaimer}</b>
            </p>

            <div className="dual-button-holder">
                <Button type="secondary" disabled={busy} onClick={onClose}>
                    {t.cancelButton}
                </Button>
                <Button
                    loading={isSubmitting}
                    onClick={onSubmit}
                    disabled={!stripe || !enabled || busy}
                >
                    {t.payButtonText}
                </Button>
            </div>
        </div>
    );
};

const PaymentForm: React.FunctionComponent<PaymentFormProps> = ({
    appointment,
    datetime,
    clinicId,
    onCompletePayment,
    onClose,
}: PaymentFormProps) => {
    const { translations } = useLanguage();
    const [isWalletProcessing, setIsWalletProcessing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isWalletAvailable, setIsWalletAvailable] = useState(false);

    const payFees = async (payment: FeePayment) => {
        if (!appointment || !datetime) return;

        const body = {
            datetime,
            clinicId,
            ...(payment.token ? { token: payment.token } : {}),
            ...(payment.paymentMethodId
                ? { paymentMethodId: payment.paymentMethodId }
                : {}),
        };

        try {
            setIsSubmitting(true);
            await Axios.put(
                ENDPOINTS.RESCHEDULE_APPPOINTEMENT_FEES(appointment.id),
                body
            );
            if (onCompletePayment) await onCompletePayment();
        } finally {
            setIsSubmitting(false);
        }
    };

    const busy = isWalletProcessing || isSubmitting;

    return (
        <PaymentMethodsBusy
            busy={busy}
            label={
                translations.upfrontPaymentSection.paymentForm.walletProcessing
            }
        >
            <ExpressWalletCheckout
                flow={WalletCheckoutFlow.LateReschedule}
                amountCents={NO_SHOW_FEE_WITH_TAX_CENTS}
                deferredNoShow={false}
                saveCard={false}
                intro={
                    translations.appointmentsSection.gatherCreditInfoModal
                        .walletIntro
                }
                onAvailabilityChange={setIsWalletAvailable}
                onProcessingChange={setIsWalletProcessing}
                onPaid={async (_method, paymentMethodId) => {
                    if (!paymentMethodId) return;
                    await payFees({ paymentMethodId });
                }}
            />
            <CardElementsProvider>
                <CardPaymentFields
                    onClose={onClose}
                    isBusy={busy}
                    payFees={payFees}
                    showCardIntro={!isWalletAvailable}
                />
            </CardElementsProvider>
        </PaymentMethodsBusy>
    );
};

export default PaymentForm;
